import { useContext, useEffect, useState } from 'react';
import Header from '../../../../components/Common/Header/Header';
import ContentApp from '../../../../components/ContentApp';
import { WelletContext } from '../../../../context/WelletContext';
import { getQueryString } from '../../../../helpers/url';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setReservation } from '../../../../actions/reservationActions';
import { CircleSpinner } from 'react-spinners-kit';
import { fetchPickups } from '../../../../services/transfer/transferService';
import ModalSelector from '../../../../components/ModalSelector/ModalSelector';
import QuantitySelector from '../../../../components/QuantitySelector/QuantitySelector';
import EventHeader from '../../../../components/Common/EventHeader/EventHeader';
import FixedButton from '../../../../components/Common/FixedButton/FixedButton';
import Button from '../../../../components/Common/Button/Button';

const TransferSelector = ({ history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const language = useSelector((state) => state.app.language);
  const welletContext = useContext(WelletContext);
  const showId = getQueryString('showId');
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(null);
  const [fromPickup, setFromPickup] = useState(null);
  const [toPickup, setToPickup] = useState(null);
  const [fromPickupList, setFromPickupList] = useState([]);
  const [toPickupList, setToPickupList] = useState([]);
  const [paxs, setPaxs] = useState(1);
  const [transferType, setTransferType] = useState({
    label: t('transfer.ROUNDTRIP'),
    value: 'ROUNDTRIP',
  });
  const transferTypeList = [
    { label: t('transfer.ROUNDTRIP'), value: 'ROUNDTRIP' },
    { label: t('transfer.ONEWAY'), value: 'ONEWAY' },
  ];

  const handleIncrementPax = () => setPaxs((prev) => prev + 1);
  const handleDecrementPax = () => setPaxs((prev) => Math.max(prev - 1, 1));

  useEffect(() => {
    const getShow = async () => {
      try {
        const result = await welletContext.apis.tickets.get(`/shows/get/`, {
          params: {
            showId,
            lang: language,
            details: false,
            reservationType: 'RESTAURANT',
            images: true,
            videos: false,
            isFeature: true,
          },
        });
        setShow(result.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    getShow();
  }, [showId, language]);

  const getPickupPoints = async (type, fromPickupId, signal) => {
    try {
      const result = await fetchPickups(welletContext, showId, type, fromPickupId, { signal });
      return result.data;
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
      return [];
    }
  };

  useEffect(() => {
    if (!transferType) return;

    const controller = new AbortController();

    const fetchFromPickups = async () => {
      setFromPickup(null);
      setToPickup(null);
      setToPickupList([]);
      setFromPickupList([]);

      try {
        const pickups = await getPickupPoints(transferType.value, undefined, controller.signal);
        setFromPickupList(pickups);
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    };

    fetchFromPickups();

    return () => controller.abort();
  }, [transferType]);

  useEffect(() => {
    if (!fromPickup) return;

    const controller = new AbortController();

    const fetchToPickups = async () => {
      setToPickup(null);
      setToPickupList([]);

      try {
        const pickups = await getPickupPoints(
          transferType.value,
          fromPickup.value,
          controller.signal,
        );
        setToPickupList(pickups);
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    };

    fetchToPickups();

    return () => controller.abort();
  }, [fromPickup, transferType]);

  const transformPickupPoints = (pickupPoints) => {
    if (pickupPoints.length > 0 && pickupPoints[0].pickups) {
      return pickupPoints.flatMap((zone) =>
        zone.pickups.map((pickup) => ({
          label: pickup.name,
          value: pickup.id,
          zoneId: zone.zoneId,
          zoneName: zone.name,
          pointType: pickup.pointType,
        })),
      );
    }
    return pickupPoints.map((pickup) => ({
      label: pickup.name,
      value: pickup.id,
      zoneId: pickup.zoneId,
    }));
  };

  const handleTransferTypeChange = (e) => {
    setTransferType(e);
    setFromPickup(null);
    setToPickup(null);
    setFromPickupList([]);
    setToPickupList([]);
  };

  const handleFromPickupChange = (e) => {
    setFromPickup(e);
    setToPickup(null);
    setToPickupList([]);
  };

  const handleContinue = () => {
    const fromZoneData = fromPickup
      ? transformPickupPoints(fromPickupList).find((pickup) => pickup.value === fromPickup.value)
      : null;
    const toZoneData = toPickup
      ? transformPickupPoints(toPickupList).find((pickup) => pickup.value === toPickup.value)
      : null;

    const trips = [];

    if (fromZoneData && toZoneData) {
      trips.push({
        fromZoneName: fromZoneData.zoneName,
        fromPickupId: fromZoneData.value,
        fromPickupName: fromZoneData.label,
        fromPointType: fromZoneData.pointType,
        toZoneName: toZoneData.zoneName,
        toPickupId: toZoneData.value,
        toPickupName: toZoneData.label,
        toPointType: toZoneData.pointType,
      });

      if (transferType.value === 'ROUNDTRIP') {
        trips.push({
          fromZoneName: toZoneData.zoneName,
          fromPickupId: toZoneData.value,
          fromPickupName: toZoneData.label,
          fromPointType: toZoneData.pointType,
          toZoneName: fromZoneData.zoneName,
          toPickupId: fromZoneData.value,
          toPickupName: fromZoneData.label,
          toPointType: fromZoneData.pointType,
        });
      }
    }

    const transfer = {
      transferType: transferType.value,
      fromZoneId: fromZoneData.zoneId,
      toZoneId: toZoneData.zoneId,
      trips,
    };

    const reservationInfo = {
      showId,
      showName: show.name,
      paxs,
      transfer,
    };

    dispatch(setReservation(reservationInfo));
    history.push({ pathname: '/select-transfer' });
  };

  const isButtonDisabled =
    !transferType || !fromPickup || (transferType.value === 'ROUNDTRIP' && !toPickup);
  const isToDisabled = toPickupList.length === 0;

  return loading ? (
    <div className='loaderDiv'>
      <CircleSpinner size={40} color='var(--color-font-primary)' />
    </div>
  ) : (
    <>
      <Header />
      <ContentApp classes='fade-up '>
        <EventHeader
          eventImg={show.bannerImageUrl}
          eventName={show.name}
          eventDescription={show.city.name}
          size='small'
        />
        <div className='mt-4 position-relative'>
          <div className='font-tiny pl-1 mb-2 fw-600 mt-3'>{t('transfer.serviceType')}</div>
          <div className='row'>
            <div className='col-6 pr-1'>
              <Button
                text={transferTypeList[0].label}
                onClick={() => handleTransferTypeChange(transferTypeList[0])}
                isClickeable={transferType.value !== transferTypeList[0].value}
                size='medium'
                className={'w-100'}
              />
            </div>
            <div className='col-6 pl-1'>
              <Button
                text={transferTypeList[1].label}
                onClick={() => handleTransferTypeChange(transferTypeList[1])}
                isClickeable={transferType.value !== transferTypeList[1].value}
                size='medium'
                className={'w-100'}
              />
            </div>
          </div>
          {/* <ModalSelector
            options={transferTypeList}
            selectedValue={transferType}
            onChange={handleTransferTypeChange}
            placeholder={transferType.label}
            enableParentReset={true}
          /> */}
        </div>

        <div className='mt-3 position-relative'>
          <div className='font-tiny pl-1 mb-2 fw-600'>{t('transfer.from')}</div>
          <ModalSelector
            options={transformPickupPoints(fromPickupList)}
            selectedValue={fromPickup}
            onChange={handleFromPickupChange}
            placeholder={t('transfer.originPlace')}
            enableParentReset={true}
            groupedData={
              fromPickupList.length > 0 && fromPickupList[0].pickups ? fromPickupList : null
            }
          />
        </div>

        <div className='mt-3 position-relative'>
          <div className='font-tiny pl-1 mb-2 fw-600'>{t('transfer.to')}</div>
          <ModalSelector
            options={transformPickupPoints(toPickupList)}
            selectedValue={toPickup}
            onChange={setToPickup}
            placeholder={t('transfer.destinationPlace')}
            disabled={isToDisabled}
            enableParentReset={true}
            groupedData={toPickupList.length > 0 && toPickupList[0].pickups ? toPickupList : null}
          />
        </div>

        <div className='mt-3 position-relative'>
          <div className=' font-tiny pl-1 mb-2 fw-600'>{t('transfer.paxsQuantity')}</div>
          <QuantitySelector
            label={t('transfer.paxs')}
            quantity={paxs}
            handleIncrement={handleIncrementPax}
            handleDecrement={handleDecrementPax}
            maxAvailable={20}
          />
        </div>

        <FixedButton backgroundColor={'#000'}>
          <Button
            text={t('continue')}
            size='large'
            className={`mt-2`}
            onClick={handleContinue}
            icon={true}
            typeIcon='arrow'
            iconColor='#000'
            disabled={isButtonDisabled}
          />
        </FixedButton>
      </ContentApp>
    </>
  );
};

export default TransferSelector;
